import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { snapEndMs, snapStartMs } from "./loadingEditPlanning";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const MAX_BASKET_NUMBER = 5;
const BASKET_OPTIONS = Array.from(
  { length: MAX_BASKET_NUMBER },
  (_unused, index) => index + 1
);

export interface EditDialogWindow {
  readonly key: string;
  readonly bucket: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly heatId: string;
  readonly basketNumber: number | null;
  readonly isNew: boolean;
}

export const LoadingWindowEditDialog = ({
  value,
  buckets,
  heatOptions,
  onSave,
  onDelete,
  onClose,
}: {
  value: EditDialogWindow | null;
  buckets: readonly string[];
  heatOptions: readonly string[];
  onSave: (window: EditDialogWindow) => void;
  onDelete: (key: string) => void;
  onClose: () => void;
}) => {
  const { t } = useTenantTranslation();
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [bucket, setBucket] = useState("");
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");
  const [heatId, setHeatId] = useState("");
  const [basketText, setBasketText] = useState("");

  // Forget the seeded window on close so reopening it re-seeds from the latest
  // times (a window keeps its key after an edit).
  if (value === null && sourceKey !== null) {
    setSourceKey(null);
  }

  // Re-seed the form when a different window opens (React's adjust-on-prop-change
  // during render, rather than an effect).
  if (value !== null && value.key !== sourceKey) {
    setSourceKey(value.key);
    setBucket(value.bucket);
    setStartText(dayjs.utc(value.startMs).format(DATETIME_FORMAT));
    setEndText(dayjs.utc(value.endMs).format(DATETIME_FORMAT));
    setHeatId(value.heatId);
    setBasketText(
      value.basketNumber === null ? "" : String(value.basketNumber)
    );
  }

  const start = dayjs.utc(startText, DATETIME_FORMAT, true);
  const end = dayjs.utc(endText, DATETIME_FORMAT, true);
  const basketNumber = Number.parseInt(basketText, 10);
  const startValid = start.isValid();
  const endValid = end.isValid() && end.valueOf() > start.valueOf();
  const basketValid =
    Number.isInteger(basketNumber) &&
    basketNumber > 0 &&
    basketNumber <= MAX_BASKET_NUMBER;
  const canSave =
    startValid && endValid && basketValid && heatId.trim().length > 0;

  const save = () => {
    if (value === null || !canSave) return;
    onSave({
      key: value.key,
      bucket,
      startMs: snapStartMs(start.valueOf()),
      endMs: snapEndMs(end.valueOf()),
      heatId: heatId.trim(),
      basketNumber,
      isNew: value.isNew,
    });
  };

  return (
    <Dialog open={value !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {value?.isNew ? t("newWindow") : t("editWindow")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select={value?.isNew ?? false}
            label={t("area")}
            value={bucket}
            onChange={(event) => {
              setBucket(event.target.value);
            }}
            disabled={!(value?.isNew ?? false)}
            size="small"
          >
            {buckets.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={`${t("start")} (UTC)`}
            value={startText}
            onChange={(event) => {
              setStartText(event.target.value);
            }}
            error={startText.length > 0 && !startValid}
            size="small"
          />
          <TextField
            label={`${t("end")} (UTC)`}
            value={endText}
            onChange={(event) => {
              setEndText(event.target.value);
            }}
            error={endText.length > 0 && !endValid}
            size="small"
          />
          <Autocomplete
            freeSolo
            options={heatOptions}
            value={heatId}
            onChange={(_event, option) => {
              setHeatId(option ?? "");
            }}
            onInputChange={(_event, input) => {
              setHeatId(input);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t("heat")} size="small" />
            )}
          />
          <TextField
            select
            label={t("basketNumber")}
            value={basketText}
            onChange={(event) => {
              setBasketText(event.target.value);
            }}
            error={basketText.length > 0 && !basketValid}
            size="small"
          >
            {BASKET_OPTIONS.map((option) => (
              <MenuItem key={option} value={String(option)}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        {value !== null && !value.isNew ? (
          <Button
            color="error"
            onClick={() => {
              onDelete(value.key);
            }}
            sx={{ mr: "auto" }}
          >
            {t("delete")}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={save} disabled={!canSave}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
